import React from 'react';
import { Phone } from 'lucide-react';
import useReveal, { reveal } from '../hooks/useReveal';
import { CONTACT } from '../data/site';

/*
  AtadaBoard does not publish a price list, so this section does not pretend to
  be one. The three towers the previous page carried have been replaced with the
  part that is actually true: every school gets the whole platform, and the
  price follows the size of the school.

  The per-tier feature lists from the old design were dropped rather than
  restated, because the differences between them were never verified.
*/
const SIZES = [
  { label: 'Small', range: 'Up to 200 students' },
  { label: 'Medium', range: '200 to 800 students' },
  { label: 'Large', range: '800+ students' },
];

const Pricing = () => {
  const [ref, isVisible] = useReveal();

  const scrollToDemo = () =>
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section id="pricing" className="ab-section ab-pricing" ref={ref}>
      <div className="ab-shell">
        <header className={`ab-pricing__head ${reveal(isVisible)}`}>
          <h2 className="ab-h2">Pricing that follows your school</h2>
          <p className="ab-lead">
            Every school gets the whole platform. What you pay depends on how many students you
            have, so a school of 80 is not priced like a school of 800.
          </p>
        </header>

        <div className={`ab-pricing__panel ${reveal(isVisible)}`} style={{ '--ab-reveal-delay': '80ms' }}>
          <dl className="ab-pricing__sizes">
            {SIZES.map((size) => (
              <div key={size.label}>
                <dt>{size.label}</dt>
                <dd className="ab-num">{size.range}</dd>
              </div>
            ))}
          </dl>

          <div className="ab-pricing__action">
            <div>
              <h3 className="ab-h3">Get a quote for your school</h3>
              <p className="ab-body">
                Tell us your student numbers and what you need running first. We will price it and
                show you the platform in the same conversation.
              </p>
            </div>

            <div className="ab-pricing__buttons">
              <button type="button" className="ab-btn ab-btn--primary" onClick={scrollToDemo}>
                Book a demo
              </button>
              <a href={CONTACT.phoneHref} className="ab-btn ab-btn--secondary">
                <Phone size={17} strokeWidth={1.75} />
                {CONTACT.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ab-pricing__head { margin-bottom: 2.25rem; max-width: 46rem; }
        .ab-pricing__head .ab-lead { margin-top: 0.875rem; }

        .ab-pricing__panel {
          border: 1px solid var(--ab-line);
          border-radius: var(--ab-r-card);
          background: var(--ab-paper);
          box-shadow: var(--ab-shadow-md);
          overflow: hidden;
        }

        .ab-pricing__sizes {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin: 0;
          background: var(--ab-blue-50);
          border-bottom: 1px solid var(--ab-blue-100);
        }

        .ab-pricing__sizes > div {
          padding: 1.375rem clamp(1.25rem, 2.5vw, 2rem);
          border-right: 1px solid var(--ab-blue-100);
        }

        .ab-pricing__sizes > div:last-child { border-right: 0; }

        .ab-pricing__sizes dt {
          font-family: var(--ab-font-display);
          font-size: 1.0625rem;
          font-weight: 600;
          letter-spacing: -0.015em;
          color: var(--ab-blue);
          margin-bottom: 0.1875rem;
        }

        .ab-pricing__sizes dd {
          margin: 0;
          font-size: 0.9375rem;
          color: var(--ab-ink-2);
        }

        .ab-pricing__action {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1.75rem;
          padding: clamp(1.75rem, 3vw, 2.5rem);
        }

        .ab-pricing__action .ab-h3 { margin-bottom: 0.5rem; }
        .ab-pricing__action .ab-body { max-width: 46ch; }

        .ab-pricing__buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }


        @media (max-width: 720px) {
          .ab-pricing__sizes { grid-template-columns: minmax(0, 1fr); }

          .ab-pricing__sizes > div {
            border-right: 0;
            border-bottom: 1px solid var(--ab-blue-100);
            padding-block: 1rem;
          }

          .ab-pricing__sizes > div:last-child { border-bottom: 0; }

          .ab-pricing__buttons { width: 100%; }
          .ab-pricing__buttons .ab-btn { flex: 1 1 auto; justify-content: center; }
        }
      `}</style>
    </section>
  );
};

export default Pricing;
